import { useState, useRef, useEffect, useCallback } from "react";

const POPOVER_VERTICAL_GAP = 12;

export interface UsePopoverReturn {
  showPopover: boolean;
  hasYOverflow: boolean;
  arrowXOffset: number | undefined;
  wrapperRef: React.RefObject<HTMLElement>;
  popoverRef: React.RefObject<HTMLDivElement>;
  closeButtonRef: React.RefObject<HTMLButtonElement>;
  handleClick: (e: React.MouseEvent) => void;
  handleClose: (e: React.MouseEvent) => void;
  handleKeyDown: (e: React.KeyboardEvent) => void;
}

/**
 * Custom hook that encapsulates all popover behavior:
 * - Open/close state
 * - Click-outside to dismiss
 * - Escape key to dismiss
 * - Dynamic positioning (below or above trigger, clamped to viewport)
 * - Arrow offset calculation
 * - Focus management (auto-focus close button on open)
 */
export function usePopover(): UsePopoverReturn {
  const [showPopover, setShowPopover] = useState(false);
  /** True when popover sits above the anchor (arrow on bottom). Name kept for PopoverWrapper API. */
  const [hasYOverflow, setHasYOverflow] = useState(false);
  const [arrowXOffset, setArrowXOffset] = useState<number | undefined>(undefined);
  const wrapperRef = useRef<HTMLElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Click-outside and Escape key handlers
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        popoverRef.current &&
        !wrapperRef.current.contains(event.target as Node) &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setShowPopover(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && showPopover) {
        setShowPopover(false);
      }
    };

    if (showPopover) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscapeKey);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("keydown", handleEscapeKey);
      };
    }
    return undefined;
  }, [showPopover]);

  // Dynamic positioning
  useEffect(() => {
    if (showPopover && wrapperRef.current && popoverRef.current) {
      // Collect every scrollable ancestor. In dev-shell-express the page scrolls
      // inside `.content-frame` (window/body never scroll), so a window scroll
      // listener never fires and the popover stays put while the text scrolls
      // under it. Listen on these so it scrolls with the content instead.
      const getScrollParents = (el: HTMLElement): HTMLElement[] => {
        const parents: HTMLElement[] = [];
        let parent = el.parentElement;
        while (parent && parent !== document.body) {
          const style = window.getComputedStyle(parent);
          const overflow = style.overflow + style.overflowY + style.overflowX;
          if (overflow.includes("scroll") || overflow.includes("auto")) {
            parents.push(parent);
          }
          parent = parent.parentElement;
        }
        return parents;
      };

      const scrollParents = getScrollParents(wrapperRef.current);

      const updatePosition = () => {
        if (!wrapperRef.current || !popoverRef.current) return;

        const viewportWidth = window.innerWidth;
        const popoverRect = popoverRef.current.getBoundingClientRect();

        // Attach the popover to a single line fragment of the trigger instead of
        // its union bounding box. A trigger that wraps across lines has a union
        // box spanning line 1's right edge to line 2's left edge, whose center
        // lands in the middle of the paragraph — aligning to it detaches both the
        // popover and its arrow from the actual words.
        const wrapperRect = wrapperRef.current.getBoundingClientRect();
        const lineRects = wrapperRef.current.getClientRects();
        const firstRect = lineRects.length > 0 ? lineRects[0] : wrapperRect;
        const lastRect = lineRects.length > 0 ? lineRects[lineRects.length - 1] : wrapperRect;

        const placedAbove =
          lastRect.bottom + POPOVER_VERTICAL_GAP + popoverRect.height > window.innerHeight - 16;
        const anchorRect = placedAbove ? firstRect : lastRect;

        const top = placedAbove
          ? anchorRect.top - popoverRect.height - POPOVER_VERTICAL_GAP
          : anchorRect.bottom + POPOVER_VERTICAL_GAP;
        let left = anchorRect.left;
        const triggerAnchorX = anchorRect.left + anchorRect.width / 2;

        // Clamp right edge first, then left edge so the left edge always wins
        // on very narrow viewports (prevents popup from sliding off-screen left).
        if (left + popoverRect.width > viewportWidth - 16) {
          left = viewportWidth - popoverRect.width - 16;
        }
        if (left < 0) {
          left = 0;
        }

        // Arrow points at the anchored line fragment; clamp inside the popover so
        // the caret never detaches from the panel.
        let arrowOffset = triggerAnchorX - left;
        const arrowMargin = 16;
        const maxArrow = Math.max(arrowMargin, popoverRect.width - arrowMargin);
        arrowOffset = Math.min(Math.max(arrowOffset, arrowMargin), maxArrow);
        setArrowXOffset(arrowOffset);

        popoverRef.current.style.top = `${top}px`;
        popoverRef.current.style.left = `${left}px`;

        // Arrow on the bottom (pointing down) when placed above, else on top.
        setHasYOverflow(placedAbove);
      };

      // Initial position
      requestAnimationFrame(() => {
        requestAnimationFrame(updatePosition);
      });

      // Update position on scroll (any scrollable ancestor) and resize
      const onScroll = () => {
        requestAnimationFrame(updatePosition);
      };
      window.addEventListener("scroll", onScroll, true);
      window.addEventListener("resize", onScroll);

      // Listen on the actual scroll container(s); window scroll capture does not
      // fire in dev-shell-express where `.content-frame` is the scroller.
      scrollParents.forEach((el) => el.addEventListener("scroll", onScroll, { passive: true }));

      // Reposition when the trigger reflows (e.g. font size / line spacing
      // changes shift it within the document).
      const resizeObserver = new ResizeObserver(() => requestAnimationFrame(updatePosition));
      resizeObserver.observe(wrapperRef.current);
      resizeObserver.observe(popoverRef.current);
      scrollParents.forEach((el) => resizeObserver.observe(el));

      // Reposition when reader settings (theme, font, line spacing, etc.) are
      // applied. Those changes mutate class / style attributes and reflow the
      // text, shifting the trigger without firing scroll or resize events.
      // Throttle via rAF to avoid cascading reflows during a settings change.
      let mutationFrame = 0;
      const mutationObserver = new MutationObserver(() => {
        cancelAnimationFrame(mutationFrame);
        mutationFrame = requestAnimationFrame(updatePosition);
      });
      mutationObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ["class", "style"],
        subtree: true,
      });

      return () => {
        window.removeEventListener("scroll", onScroll, true);
        window.removeEventListener("resize", onScroll);
        scrollParents.forEach((el) => el.removeEventListener("scroll", onScroll));
        resizeObserver.disconnect();
        mutationObserver.disconnect();
        cancelAnimationFrame(mutationFrame);
      };
    }
    return undefined;
  }, [showPopover]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowPopover((prev) => !prev);
  }, []);

  const handleClose = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    wrapperRef.current?.focus();
    setShowPopover(false);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleClick(e as any);
      }
    },
    [handleClick],
  );

  return {
    showPopover,
    hasYOverflow,
    arrowXOffset,
    wrapperRef,
    popoverRef,
    closeButtonRef,
    handleClick,
    handleClose,
    handleKeyDown,
  };
}
